import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronUp, Plus } from 'lucide-react';

import { Button } from '@/components/ui';
import { useCountries } from '@/hooks/smswiser/core/useSMSWiser';
import type { TagRuleCountryThreshold } from '@/hooks';
import { cn } from '@/lib/utils';

import { CountryOverrideRow } from './CountryOverrideRow';
import { CountryThresholdCollapsed } from './CountryThresholdCollapsed';
import {
  countAssignedCountries,
  countriesAvailableForGroup,
  CountBadge,
  CountryThresholdTableHead,
  flattenUIGroups,
  MAX_COUNTRY_THRESHOLD_ROWS,
  othersCountryCount,
  rowsToUIGroups,
  type CountryThresholdUIGroup,
} from './country-threshold-parts';

export const CountryThresholdOverrides: FC<{
  rows: TagRuleCountryThreshold[];
  onChange: (next: TagRuleCountryThreshold[]) => void;
  className?: string;
  fields?: ReadonlyArray<{ key: string; label: string }>;
}> = ({ rows, onChange, className, fields }) => {
  const [expanded, setExpanded] = useState(() => rows.length > 0);
  const { data: countries = [], isLoading } = useCountries();
  const sorted = useMemo(
    () => [...countries].sort((a, b) => a.name.localeCompare(b.name)),
    [countries],
  );

  const serialized = JSON.stringify(rows);
  const lastSerialized = useRef(serialized);
  const [groups, setGroups] = useState(() => rowsToUIGroups(rows));

  useEffect(() => {
    if (rows.length === 0) setExpanded(false);
    if (serialized !== lastSerialized.current) {
      lastSerialized.current = serialized;
      setGroups(rowsToUIGroups(rows));
    }
  }, [rows, rows.length, serialized]);

  const assigned = countAssignedCountries(rows);
  const atLimit = assigned >= MAX_COUNTRY_THRESHOLD_ROWS;

  const commitNext = useCallback(
    (updater: (prev: CountryThresholdUIGroup[]) => CountryThresholdUIGroup[]) => {
      setGroups((prev) => {
        const next = updater(prev);
        onChange(flattenUIGroups(next));
        return next;
      });
    },
    [onChange],
  );

  const hasEmptyDraft = groups.some((g) => g.countryKeys.length === 0);
  const disableAdd = atLimit || hasEmptyDraft;

  const addRow = useCallback(() => {
    commitNext((prev) => {
      const flat = flattenUIGroups(prev);
      if (countAssignedCountries(flat) >= MAX_COUNTRY_THRESHOLD_ROWS) return prev;
      if (prev.some((g) => g.countryKeys.length === 0)) return prev;
      return [...prev, { id: `draft-${Date.now()}`, countryKeys: [], value: 0 }];
    });
  }, [commitNext]);

  const patchGroup = useCallback(
    (id: string, patch: Partial<Pick<CountryThresholdUIGroup, 'countryKeys' | 'value' | 'compareTo'>>) => {
      commitNext((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)));
    },
    [commitNext],
  );

  const removeGroup = useCallback(
    (id: string) => commitNext((prev) => prev.filter((g) => g.id !== id)),
    [commitNext],
  );

  if (!expanded) {
    return (
      <CountryThresholdCollapsed
        rowCount={assigned}
        isLoading={isLoading}
        disableAdd={disableAdd}
        className={className}
        onExpand={() => setExpanded(true)}
        onAdd={() => {
          setExpanded(true);
          addRow();
        }}
      />
    );
  }

  return (
    <div className={cn('border-t border-border/60 bg-muted/10', className)}>
      <div className="flex items-center justify-between gap-2 border-b border-border/40 px-3 py-1.5">
        <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate font-medium text-foreground">Overrides</span>
          <CountBadge n={assigned} />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          onClick={() => setExpanded(false)}
        >
          <ChevronUp className="h-3.5 w-3.5" />
          Hide
        </Button>
      </div>

      {groups.length > 0 ? <CountryThresholdTableHead /> : null}
      {groups.map((g) => (
        <CountryOverrideRow
          key={g.id}
          group={g}
          options={countriesAvailableForGroup(sorted, groups, g.id)}
          maxKeys={Math.max(0, MAX_COUNTRY_THRESHOLD_ROWS - othersCountryCount(groups, g.id))}
          isLoading={isLoading}
          onPatch={patchGroup}
          onRemove={removeGroup}
          fields={fields}
        />
      ))}

      <div className="border-t border-border/40 px-3 py-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={addRow}
          disabled={disableAdd || isLoading}
        >
          <Plus className="h-3.5 w-3.5" />
          Add row
        </Button>
      </div>
    </div>
  );
};
